import { type fabric } from 'fabric';
import { MIN_ROW_HEIGHT, MIN_COLUMN_WIDTH } from '../objects/Table';
import type Table from '../objects/Table';
import ControlBase from '../fabric/ControlBase';
import { type IPoint } from 'fabric/fabric-impl';
import { resizeWithSmartGuides } from '../objects/SmartGuideBehaviors';

class TableVectorResizeControl extends ControlBase {
  // Whether this control lies on a row or column
  isRow: boolean;
  // Index of vector in table
  index: number;
  // Flag for visibility of control in merged cells. Set in table. Cant set
  // visibility in control, because once hidden, control events no longer fire.
  shouldHide = false;

  constructor(index: number, isRow: boolean) {
    super({});
    this.isRow = isRow;
    this.actionName = `Resize ${this.isRow ? 'row' : 'column'}`;
    this.index = index;
    this.cursorStyle = isRow ? 'row-resize' : 'col-resize';
  }

  vectorResizeControlPosition(object: fabric.Object): IPoint {
    const table = object as Table;
    const key = table.getVectorDimensionKey(this.index, this.isRow);
    const tableDimensions = table._getNonTransformedDimensions();
    let dim = 0;
    for (let i = 0; i < this.index; i++) {
      dim += table[table.getVectorDimensionKey(i, this.isRow)];
    }
    dim += table[key];
    const x = this.isRow ? 0 : -0.5 + dim / tableDimensions.x;
    const y = this.isRow ? -0.5 + dim / tableDimensions.y : 0;
    return { x, y };
  }

  getMouseDownHandler(): fabric.ControlMouseEventHandler {
    return (eventData, transform) => {
      const object = transform.target as Table;
      object.clearSelectedCells();
      object.toggleResizing(true);
      object.fire('scaling', { e: eventData, transform });
      return true;
    };
  }

  mouseUpHandler(
    eventData: MouseEvent,
    transformData: fabric.Transform
  ): boolean {
    if (transformData.target) {
      const object = transformData.target as Table;
      object.canvas.controller.undoManager.completeUndoGroup();
      object.toggleResizing(false);
      object.fire('scaled');
      return true;
    }
    return false;
  }

  positionHandler(
    dim: { x: number; y: number },
    finalMatrix: any,
    fabricObject: fabric.Object,
    currentControl: fabric.Control
  ): fabric.Point {
    const zoom = fabricObject.canvas?.getZoom() || 1;
    const { x, y } = this.vectorResizeControlPosition(fabricObject);
    this.x = x;
    this.y = y;

    const thickness = fabricObject.cornerSize;
    const touchThickness = fabricObject.touchCornerSize;
    const margin = fabricObject.touchCornerSize;
    if (this.isRow) {
      this.sizeY = thickness;
      this.sizeX = fabricObject.width * fabricObject.scaleX * zoom;
      this.touchSizeY = touchThickness;
      this.touchSizeX = this.sizeX;
    } else {
      this.sizeY = fabricObject.height * fabricObject.scaleY * zoom - margin;
      this.sizeX = thickness;
      this.touchSizeY = this.sizeY;
      this.touchSizeX = touchThickness;
    }
    return super.positionHandler(
      dim,
      finalMatrix,
      fabricObject,
      currentControl
    );
  }

  getVisibility(fabricObject: any, controlKey: any): boolean {
    return !this.shouldHide && super.getVisibility(fabricObject, controlKey);
  }

  getActionHandler(): fabric.ControlMouseEventHandler {
    const handler = (
      eventData: MouseEvent,
      transformData: fabric.Transform,
      x: number,
      y: number
    ): boolean => {
      const { index, isRow } = this;
      const object = transformData.target as Table;
      const vectorKey = object.getVectorDimensionKey(index, isRow);
      const tableKey = isRow ? 'height' : 'width';
      const minDimension = isRow ? MIN_ROW_HEIGHT : MIN_COLUMN_WIDTH;
      const rect = object.getVectorRect({
        index: this.index,
        isRow: this.isRow,
        scaled: true,
      });
      if (rect) {
        const { left, top, width, height } = rect;
        let vectorShift = isRow ? -(y - (top + height)) : -(x - (left + width));

        if (vectorShift > 0) {
          const min = object.minSpaceAvailable(index, isRow);
          if (min !== null && vectorShift > min) {
            vectorShift = min;
          }
        }

        let vectorSize = object[vectorKey] - vectorShift;
        if (vectorSize < minDimension) {
          vectorShift = object[vectorKey] - minDimension;
          vectorSize = minDimension;
        }

        object.shiftObjectsAfterVectorIndex(index, -vectorShift, isRow);
        object.set(vectorKey, vectorSize);
        object.set(tableKey, object[tableKey] - vectorShift);

        return vectorShift !== 0;
      }

      return false;
    };

    const axis = this.isRow ? 'h' : 'v';
    return resizeWithSmartGuides(handler, [axis]);
  }

  render(): void {
    // render(
    //   ctx: CanvasRenderingContext2D,
    //   left: number,
    //   top: number,
    //   styleOverride,
    //   fabricObject
    // ): void {
    // Debug only
    // ctx.save();
    // ctx.lineJoin = 'round';
    // ctx.fillStyle = 'black';
    // ctx.strokeStyle = '#999999';
    // ctx.lineWidth = 3;
    // ctx.fillRect(
    //   left - (fabricObject.width / 2) * this.isRow,
    //   top - (fabricObject.height / 2) * !this.isRow,
    //   3 + fabricObject.width * this.isRow,
    //   3 + fabricObject.height * !this.isRow
    // );
    // ctx.restore();
  }
}
export default TableVectorResizeControl;
